import DeleteIcon from '@src/svgs/DeleteIcon';
import PencilIcon from '@src/svgs/PencilIcon';
import { useState } from 'react';
import styled from 'styled-components';

import CollectionFormModal from './CollectionFormModal';
import { DangerButton, FormActions, GhostButton } from './formControls';

import type {
  TranslationMemoryCollection,
  TranslationMemoryCollectionInput,
} from '@src/repositories/TranslationMemoryCollectionRepository';

interface Props {
  collection: TranslationMemoryCollection;
  selected: boolean;
  onSelect: () => void;
  /** Resolve `null` on success or an error message to surface inline. */
  onRename: (input: TranslationMemoryCollectionInput) => Promise<string | null>;
  onDelete: () => Promise<string | null>;
}

type Mode = 'view' | 'edit' | 'confirm-delete';

const CollectionRow = ({
  collection,
  selected,
  onSelect,
  onRename,
  onDelete,
}: Props) => {
  const [mode, setMode] = useState<Mode>('view');
  const [busy, setBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const confirmDelete = async () => {
    setBusy(true);
    setDeleteError(null);
    const message = await onDelete();
    setBusy(false);
    if (message) setDeleteError(message);
  };

  if (mode === 'confirm-delete') {
    return (
      <tr>
        <ConfirmCell>
          <ConfirmBox role="alertdialog">
            <p>
              Delete <strong>{collection.name}</strong>? Its entries aren&apos;t
              removed — they move to Default (uncategorized).
            </p>
            {deleteError && <ErrorText role="alert">{deleteError}</ErrorText>}
            <FormActions>
              <GhostButton
                type="button"
                onClick={() => {
                  setDeleteError(null);
                  setMode('view');
                }}
                disabled={busy}
              >
                Cancel
              </GhostButton>
              <DangerButton
                type="button"
                onClick={confirmDelete}
                disabled={busy}
              >
                Delete
              </DangerButton>
            </FormActions>
          </ConfirmBox>
        </ConfirmCell>
      </tr>
    );
  }

  return (
    <>
      <Row $selected={selected} onClick={onSelect}>
        <td>
          <Cell>
            <SelectArea>
              <span className="name">{collection.name}</span>
              {collection.description && (
                <span className="description" title={collection.description}>
                  {collection.description}
                </span>
              )}
            </SelectArea>
            <Actions onClick={e => e.stopPropagation()}>
              <IconButton
                type="button"
                aria-label="Rename collection"
                onClick={() => setMode('edit')}
              >
                <PencilIcon width="13" height="14" strokeColor="currentColor" />
              </IconButton>
              <IconButton
                type="button"
                aria-label="Delete collection"
                onClick={() => setMode('confirm-delete')}
              >
                <DeleteIcon width="14" height="14" strokeColor="currentColor" />
              </IconButton>
            </Actions>
          </Cell>
        </td>
      </Row>
      {mode === 'edit' && (
        <CollectionFormModal
          mode="edit"
          initialName={collection.name}
          initialDescription={collection.description ?? ''}
          onClose={() => setMode('view')}
          onSubmit={onRename}
        />
      )}
    </>
  );
};

export default CollectionRow;

const Row = styled.tr<{ $selected: boolean }>`
  cursor: pointer;
  border-bottom: 1px solid var(--border);
  ${props => props.$selected && `box-shadow: inset 2px 0 0 var(--primary);`}

  &:hover {
    background: var(--surface-elevated);
  }

  &:hover [data-row-actions] {
    opacity: 1;
    pointer-events: auto;
  }

  > td {
    padding: 10px 12px;
  }
`;

const Cell = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SelectArea = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  .name {
    font-size: var(--text-sm);
    font-weight: var(--font-weight-medium);
    color: var(--foreground);
    overflow-wrap: anywhere;
  }

  .description {
    font-size: var(--text-xs);
    color: var(--text-muted);
    overflow-wrap: anywhere;
    /* Clamp to two lines; the full text is available on hover (title attr). */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const Actions = styled.div.attrs({ 'data-row-actions': true })`
  /* Overlay the icons on hover instead of reserving layout space, so the
     collection name/description use the row's full width when idle. */
  position: absolute;
  right: 0;
  /* Stretch the full row height and flex-center so the icons land at the
     midpoint between the row's divider lines regardless of summary length. */
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 2px;
  padding-left: 12px;
  /* Fade the row's hover background in from the left so the icons stay legible
     over any text they cover. */
  background: linear-gradient(
    to right,
    transparent,
    var(--surface-elevated) 12px
  );
  opacity: 0;
  pointer-events: none;
  transition: opacity var(--transition-fast);
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: none;
  border: none;
  border-radius: var(--radius-input);
  cursor: pointer;
  color: var(--text-secondary);

  &:hover {
    background: var(--border);
    color: var(--foreground);
  }
`;

const ConfirmCell = styled.td`
  padding: 10px 12px;
`;

const ConfirmBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid var(--destructive);
  border-radius: var(--radius-input);

  > p {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--text-secondary);
  }
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: var(--text-xs);
  color: var(--destructive);
`;
